import { useConfirm } from "material-ui-confirm";
import { useEffect, useRef, useContext } from "react";
import { UnsavedPageWarningContext } from "../contexts/UnsavedPageWarningContext";

/**
 * A wrapper around `useState` which enables the `onbeforeunload`
 * functionality and integrates with UnsavedPageWarningContext
 */
export const useSaved = (
  initialValue: boolean,
  closeForm?: (...args: any) => void,
  message?: string,
  formKey?: string,
  disableSave: boolean = false,
) => {
  const confirm = useConfirm();
  const savedRef = useRef(initialValue);

  // Get the context value - returns the default (no-op) if provider not available
  const context = useContext(UnsavedPageWarningContext);
  const addUnSavePageWarning = context?.addUnSavePageWarning;

  // Register initial state with context — only set the isUnsaved entry,
  // not the global message/title, to avoid cascading re-renders.
  useEffect(() => {
    if (addUnSavePageWarning && formKey) {
      addUnSavePageWarning({
        isUnsaved: [formKey, !initialValue], // !initialValue because setSaved(true) means saved, so unsaved = false
        disableSave,
      });
    }
  }, []);

  const setSaved = (value: boolean) => {
    savedRef.current = value;

    window.onbeforeunload = !savedRef.current ? () => true : null;

    if (addUnSavePageWarning && formKey) {
      addUnSavePageWarning({
        isUnsaved: [formKey, !value],
        disableSave,
      });
    }
  };

  useEffect(
    () => () => {
      setSaved(true);
      window.onbeforeunload = null;
    },
    [],
  );

  const requestClose = async () => {
    if (savedRef.current) {
      if (addUnSavePageWarning && formKey) {
        addUnSavePageWarning({ isUnsaved: [formKey, false], disableSave });
      }
      window.onbeforeunload = null;
      if (closeForm) closeForm();
      return true;
    }

    const res = await confirm({
      title: "Unsaved changes",
      description: message ?? "You have unsaved changes. Are you sure you want to close?",
      confirmationText: "Close anyway",
      cancellationText: "Cancel",
    })
      .then(() => true)
      .catch(() => false);

    if (res) {
      setSaved(true);
      if (closeForm) closeForm();
      return true;
    }
    return false;
  };

  const requestCloseByProp = (onCloseForm: (...args: any) => void) => {
    if (savedRef.current) return onCloseForm();

    confirm({
      description: "Any unsaved changes would be lost!",
    })
      .then(() => {
        setSaved(true);
        onCloseForm();
      })
      .catch(() => {});
  };

  return {
    saved: savedRef.current,
    setSaved,
    requestClose,
    requestCloseByProp,
    allowNavigation: context?.allowNavigation,
  };
};
